import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CurrentFaceShape,
  CustomerFaceAssessmentSummary,
  FaceCandidateInput,
  FaceShape,
  MeasurementSessionSummary,
} from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';

export const faceKeys = {
  all: ['face-assessments'] as const,
  forCustomer: (customerId: string) => [...faceKeys.all, 'customer', customerId] as const,
  current: (customerId: string) => [...faceKeys.all, 'current', customerId] as const,
};

export function useFaceAssessmentsQuery(customerId: string | undefined) {
  return useQuery({
    queryKey: faceKeys.forCustomer(customerId ?? ''),
    queryFn: () =>
      api.get<CustomerFaceAssessmentSummary[]>(`/customers/${customerId ?? ''}/face-assessments`),
    enabled: Boolean(customerId),
  });
}

/** The narrow contract a later frame recommendation reads: a confirmed decision, or nothing. */
export function useCurrentFaceShapeQuery(customerId: string | undefined) {
  return useQuery({
    queryKey: faceKeys.current(customerId ?? ''),
    queryFn: () => api.get<CurrentFaceShape | null>(`/customers/${customerId ?? ''}/face-shape`),
    enabled: Boolean(customerId),
  });
}

/** What an optician sends: their decision, and for a mixed face the two shapes it sits between. */
export interface FaceAssessmentInput {
  confirmedShape: FaceShape;
  componentA?: FaceShape;
  componentB?: FaceShape;
}

export function useRecordFaceAssessmentMutation(customerId: string) {
  const queryClient = useQueryClient();

  return useIdempotentMutation<CustomerFaceAssessmentSummary, FaceAssessmentInput>(
    (payload, config) =>
      api.post<CustomerFaceAssessmentSummary>(
        `/customers/${customerId}/face-assessments/manual`,
        payload,
        config,
      ),
    { onSuccess: () => void queryClient.invalidateQueries({ queryKey: faceKeys.all }) },
  );
}

export function useVoidFaceAssessmentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<CustomerFaceAssessmentSummary>(`/face-assessments/${id}/void`, { reason }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: faceKeys.all }),
  });
}

// ---------------------------------------------------------------------------
// The camera path
// ---------------------------------------------------------------------------

/**
 * Creating a FACE session. Purpose is explicit: a PD session and a face session take different payloads.
 *
 * `opening` names the dialog opening that asks, and is never sent. It makes each opening its own operation with its own
 * idempotency key: two openings for the same customer and source would otherwise share one, and the server would hand a
 * reopened dialog the session an abandoned opening made — or refuse it as a request still in progress.
 */
export function useCreateFaceSessionMutation() {
  return useIdempotentMutation<
    MeasurementSessionSummary,
    {
      customerId: string;
      requestedSource: 'LIVE_CAMERA' | 'PHONE_QR' | 'PHOTO';
      opening: number;
    }
  >(({ customerId, requestedSource }, config) =>
    api.post<MeasurementSessionSummary>(
      '/measurements/sessions',
      { customerId, requestedSource, purpose: 'FACE' },
      config,
    ),
  );
}

export function useStartFaceSessionMutation() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      api.post<MeasurementSessionSummary>(`/measurements/sessions/${sessionId}/start`),
  });
}

/** What this device suggests. A suggestion only: it never becomes the customer's face shape by itself. */
export function useSubmitFaceCandidateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, candidate }: { sessionId: string; candidate: FaceCandidateInput }) =>
      api.post<MeasurementSessionSummary>(
        `/measurements/sessions/${sessionId}/face-candidate`,
        candidate,
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: faceKeys.all }),
  });
}

/**
 * The human decision that ends a FACE session.
 *
 * The body carries only what the person chose. The suggestion, its scores, its reasons and its
 * versions come from the candidate the server already holds for this session.
 */
export function useConfirmFaceShapeMutation() {
  const queryClient = useQueryClient();

  return useIdempotentMutation<
    MeasurementSessionSummary,
    { sessionId: string; confirmedShape: FaceShape; componentA?: FaceShape; componentB?: FaceShape }
  >(
    ({ sessionId, ...decision }, config) =>
      api.post<MeasurementSessionSummary>(
        `/measurements/sessions/${sessionId}/confirm-face`,
        decision,
        config,
      ),
    { onSuccess: () => void queryClient.invalidateQueries({ queryKey: faceKeys.all }) },
  );
}
